import {
	Outlet,
	type ShouldRevalidateFunctionArgs,
	useOutletContext,
} from '@remix-run/react'
import * as React from 'react'

import { z } from 'zod'
import { NEWSLETTER_FORM } from '~/routes/($lang).newsletter/newsletter.component'
import {
	type Context,
	type FormEnum,
	ROOT_INPUTS,
} from '~/routes/_layout/root.component'
import { generateItemFormFetcherKey } from '~/utils/client/generate-item-form-fetcher-key.ts'
import { generateItemFormId } from '~/utils/client/generate-item-form-id.ts'
import { schemaDefault } from '~/utils/form-defaults.ts'

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	routeProfileAddresses = 'route-profile-addresses',
}

/* -------------------------------------------------------------------------------------------------
 * Route loader
 * -----------------------------------------------------------------------------------------------*/

enum ROUTE_LOADER {
	root = 'root',
	current = 'routes/($lang).profile.addresses',
}

/* -------------------------------------------------------------------------------------------------
 * Search params
 * -----------------------------------------------------------------------------------------------*/

enum SEARCH_PARAMS {
	addressCreated = 'address-created',
	addressEdited = 'address-edited',
	addressRemoved = 'address-removed',
	defaultAddressChanged = 'default-address-changed',
	page = 'page',
	itemsPerPage = 'itemsPerPage',
	q = 'q',
	firstName = 'firstName',
	lastName = 'lastName',
	phoneNumber = 'phoneNumber',
	company = 'company',
	countryCode = 'countryCode',
	street = 'street',
	city = 'city',
	postcode = 'postcode',
}

/* -------------------------------------------------------------------------------------------------
 * Inputs
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
	defaultAddress = 'default-address',
	removeAddress = 'remove-address',
}

/* -------------------------------------------------------------------------------------------------
 * Set default address form
 * -----------------------------------------------------------------------------------------------*/

const FORM_SET_DEFAULT_ADDRESS: FormEnum = {
	generateId: ({ id }) => generateItemFormId('set-default-address-form', id),
	action: '/profile/addresses',
	generateFetcherKey: ({ id }) =>
		generateItemFormFetcherKey('set-default-address-form', id),
	subaction: 'set-default-address-form',
	redirect: '/profile/addresses',
} as const

const setDefaultAddressSchema = z.object({
	...schemaDefault,
	...{
		[INPUTS.defaultAddress]: z.string({
			required_error: 'validation.email.required',
		}),
	},
})

/* -------------------------------------------------------------------------------------------------
 * Remove address form
 * -----------------------------------------------------------------------------------------------*/

const FORM_REMOVE_ADDRESS: FormEnum = {
	generateId: ({ id }) => generateItemFormId('remove-address-form', id),
	action: '/profile/addresses',
	generateFetcherKey: ({ id }) =>
		generateItemFormFetcherKey('remove-address-form', id),
	subaction: 'remove-address-form',
	redirect: '/profile/addresses',
} as const

const removeAddressSchema = z.object({
	...schemaDefault,
	[INPUTS.removeAddress]: z.number(),
})

/* -------------------------------------------------------------------------------------------------
 * Handle
 * -----------------------------------------------------------------------------------------------*/

const handle = { i18n: [...Object.values(I18NEXT)] }

/* -------------------------------------------------------------------------------------------------
 * Should revalidate
 * -----------------------------------------------------------------------------------------------*/

const shouldRevalidate = ({
	formData,
	defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) => {
	if (formData?.get(ROOT_INPUTS.subaction) === NEWSLETTER_FORM.subaction) {
		return false
	}

	return defaultShouldRevalidate
}

/* -------------------------------------------------------------------------------------------------
 * Component
 * -----------------------------------------------------------------------------------------------*/

const Component: React.FC = () => (
	<Outlet context={useOutletContext<Context>()} />
)
Component.displayName = 'Component'

/* -----------------------------------------------------------------------------------------------*/

export {
	Component,
	shouldRevalidate,
	handle,
	I18NEXT as PROFILE_ADDRESSES_I18NEXT,
	ROUTE_LOADER as PROFILE_ADDRESSES_ROUTE_LOADER,
	FORM_SET_DEFAULT_ADDRESS as PROFILE_ADDRESSES_FORM_SET_DEFAULT_ADDRESS,
	FORM_REMOVE_ADDRESS as PROFILE_ADDRESSES_FORM_REMOVE_ADDRESS,
	SEARCH_PARAMS as PROFILE_ADDRESSES_SEARCH_PARAMS,
	INPUTS as PROFILE_ADDRESSES_INPUTS,
	setDefaultAddressSchema as profileAddressesSetDefaultAddressSchema,
	removeAddressSchema as profileAddressesRemoveAddressSchema,
}
